import React, { useState } from 'react';
import { Box, Button, Typography, Card, CardContent } from '@mui/material';
import CreditCard from '@mui/icons-material/CreditCard';
import { PayStack } from './Paystack';
import PaymentModal from './PaymentModal';
import CustomButton from '../Button/CustomButton';
import AddIcon from '../CustomSVGIcons/AddIcon';
import { strings } from '../../constants/strings';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import MasterCard from '../CustomSVGIcons/MasterCard';
import VisaCard from '../CustomSVGIcons/VisaCard';
import { getJWtDetails } from '../../utils/AppUtils';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { en_config } from '../../config';

interface IAddCard {
    isNonUs: boolean;
    publicKey: string;
    amount: number;
}

const AddCard: React.FC<IAddCard> = ({ isNonUs, publicKey, amount }) => {
    const [open, setOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const { user } = getJWtDetails();

    return (
        <Card
            sx={{
                maxWidth: 400,
                margin: 'auto',
                textAlign: 'center',
                padding: 2,
                justifyContent: 'center',
                backgroundColor: theme[themeStyle].deep_grey_lighter,
            }}>
            <PaymentModal onClose={() => setOpen(!open)} open={open} amount={amount} />
            <CardContent>
                <Box display="flex" justifyContent="center" gap={1} marginBottom={2}>
                    <MasterCard height={'50px'} width={'50px'} />
                    <VisaCard height={'50px'} width={'50px'} />
                </Box>
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 17, color: theme[themeStyle].tag_white }}>
                    Add a credit card for payment
                </Typography>
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: theme[themeStyle].tag_white }}
                    color="textSecondary"
                    sx={{ marginY: 2 }}>
                    This credit card will be used by default for billing.
                </Typography>
                <Box display="flex" justifyContent="center">
                    {isNonUs && user ? (
                        <PayStack
                            email={user.email}
                            amount={en_config.PAYSTACK_CHARGE} // Charge N50 to authenticate
                            publicKey={publicKey}
                            isAddNewCardButton={false}
                        />
                    ) : (
                        <CustomButton
                            label={strings.add_new_card}
                            onClick={() => setOpen(!open)}
                            withIcon
                            customIcon={
                                <AddIcon
                                    style={{ marginLeft: '4px' }}
                                    height={'20px'}
                                    width={'20px'}
                                    fill={theme[themeStyle].zeus_dark}
                                />
                            }
                        />
                    )}
                </Box>
            </CardContent>
            {isNonUs && (
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 8, color: theme[themeStyle].tag_white }}>
                    We will initiate a small N{en_config.PAYSTACK_CHARGE / 100} charge. This will be refunded
                </Typography>
            )}
        </Card>
    );
};

const DeleteCard = () => {
    return (
        <Card sx={{ maxWidth: 400, margin: 'auto', textAlign: 'center', padding: 2 }}>
            <CardContent>
                <Typography variant="h6" fontWeight="bold">
                    Are you sure you want to delete this card?
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ marginY: 2 }}>
                    This credit card will be deleted.
                </Typography>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: 2,
                        borderRadius: 1,
                        marginBottom: 2,
                    }}>
                    <CreditCard color="action" />
                    <Typography variant="body2">Visa ending in 7830</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                    <Button variant="outlined" color="primary" fullWidth>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" fullWidth>
                        Delete
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export { DeleteCard, AddCard };
